// The signed-in session for the whole app (spec/dashboard.md section 3.2): the API client reads the token from session.ts,
// and a sign-in or sign-out in another tab is mirrored here through the `storage` event on the shared session key.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiClient } from '../api/client';
import type { Session } from '../api/types';
import { clearSession, loadSession, parseSession, saveSession, SESSION_KEY, type StoredSession } from './session';

export interface AuthContextValue {
  session: StoredSession | null;
  client: ApiClient;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** stores a session another route opened (an accepted invite, a completed password reset; M7 slice 3) */
  adoptSession: (session: Session) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children, baseUrl = '' }: { children: ReactNode; baseUrl?: string }) {
  const [session, setSession] = useState<StoredSession | null>(() => loadSession());

  // another tab signed in, switched account or signed out: follow it (a `null` key is a storage.clear())
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== SESSION_KEY && e.key !== null) return;
      if (e.key === null) setSession(loadSession());
      else if (e.newValue === null) setSession(null);
      else setSession(parseSession(e.newValue));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const client = useMemo(
    () =>
      new ApiClient({
        baseUrl,
        getToken: () => loadSession()?.token ?? null,
        onUnauthenticated: () => {
          clearSession();
          setSession(null);
        },
      }),
    [baseUrl],
  );

  const adoptSession = useCallback((s: Session) => {
    const stored = { token: s.token, expiresAt: s.expiresAt ?? '', email: s.user.email };
    saveSession(stored);
    setSession(stored);
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      adoptSession(await client.signIn(email, password));
    },
    [client, adoptSession],
  );

  const signUp = useCallback(
    async (email: string, password: string, displayName: string) => {
      const s = await client.signUp(email, password, displayName);
      const stored = { token: s.token, expiresAt: s.expiresAt ?? '', email: s.user.email };
      saveSession(stored);
      setSession(stored);
    },
    [client],
  );

  const signOut = useCallback(async () => {
    try {
      await client.signOut();
    } finally {
      clearSession();
      setSession(null);
    }
  }, [client]);

  const value = useMemo(() => ({ session, client, signIn, signUp, signOut, adoptSession }), [session, client, signIn, signUp, signOut, adoptSession]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
